import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { AuthFacade } from '@core/services/auth-facade';
import { environment } from '@environments/environment';
import { HeroMap } from '../hero-map/hero-map';

interface WorkflowStage {
  icon: string;
  title: string;
  description: string;
}

interface WorkflowColumn {
  label: string;
  items: readonly string[];
  tone: string;
}

interface WorkflowConnection {
  source: string;
  relationship: string;
  target: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-hero-section',
  imports: [RouterLink, MatButtonModule, MatIconModule, HeroMap],
  templateUrl: './hero-section.html',
  styleUrl: './hero-section.css',
})
export class HeroSection {
  readonly authFacade = inject(AuthFacade);
  readonly unauthenticatedRoute =
    environment.name === 'production' ? '/early-access' : '/auth/signup';
  readonly workflowQuestion =
    'How can we reduce energy use in data centers without compromising computing performance?';

  readonly stages: readonly WorkflowStage[] = [
    {
      icon: 'target',
      title: 'Define',
      description: 'Clarify the challenge, goals and constraints that matter.',
    },
    {
      icon: 'search',
      title: 'Explore',
      description:
        'Investigate the highest-value questions across your knowledge and external research.',
    },
    {
      icon: 'lightbulb',
      title: 'Develop',
      description:
        'Turn evidence into new possibilities through analogy, recombination and structured ideation.',
    },
    {
      icon: 'bar_chart',
      title: 'Prioritize',
      description: 'Connect, challenge and rank the strongest opportunities to pursue.',
    },
  ];

  readonly workflowColumns: readonly WorkflowColumn[] = [
    {
      label: 'Research',
      items: ['Cooling systems', 'Server utilization', 'Workload scheduling'],
      tone: 'landing-dot--neutral',
    },
    {
      label: 'Evidence',
      items: [
        'Main energy loss points',
        'Performance constraints',
        'Existing infrastructure limits',
      ],
      tone: 'landing-dot--neutral-muted',
    },
    {
      label: 'Ideas',
      items: [
        'Adaptive cooling zones',
        'Workload-aware energy orchestration',
        'Waste-heat reuse loops',
      ],
      tone: 'landing-dot--primary',
    },
  ];

  readonly workflowConnections: readonly WorkflowConnection[] = [
    {
      source: 'Cooling systems',
      relationship: 'drive',
      target: 'Main energy loss points',
    },
    {
      source: 'Workload scheduling',
      relationship: 'unlocks',
      target: 'Adaptive cooling zones',
    },
    {
      source: 'Infrastructure limits',
      relationship: 'constrains',
      target: 'Waste-heat reuse loops',
    },
  ];
}
